// The numbers (WEB_PLAN.md §5.3, §5.10): one implementation of `AppStore.metricValue` (GroundTeam/Store/AppStore.swift:858)
// that matches `public.team_metrics` (supabase/migrations/20260928120000_groundteam_sync.sql:1331) for every person,
// plus repSummary (Team+Store.swift), leaderboards, goals, badges, the team feed (Reports+Store.teamFeed) and the
// ReportEngine summary (GroundTeam/Services/ReportEngine.swift).
//
// Rules (both apps and the SQL):
// - an activity counts only when `status == completed` and its effective date (completedAt ?? scheduledAt ??
//   createdAt) is within [from, to];
// - visits = type visit; contacts = visits whose result `countsAsContact`; appointments = type meeting, or any
//   activity whose result's name contains "appointment" (case-insensitive); calls / emails / texts by type;
// - deals = records with `wonAt` in range, credited to `ownerId`; revenue = the sum of their `value`;
// - newRecords = records with `createdAt` in range, credited to `createdBy` and to `ownerId` (once per record).
// With no user (company totals) everything counts, unowned and unattributed items included, as `metricValue(_, nil)`.

import type { ActivityType, GoalMetric, GoalPeriod } from '@/contracts/enums';
import type {
  ActivityPayload,
  BadgePayload,
  DayLogPayload,
  GoalPayload,
  PipelinePayload,
  RecordPayload,
  UserPayload,
  VisitResultPayload,
} from '@/contracts/models';
import type { MetricsRow } from '@/contracts/wire';
import { endOfDay, isSameDay, resolveWeekStart, startOfDay, startOfWeek, type WeekOptions } from './calendar';
import { effectiveMs, isVerified } from './derived';
import { goalPeriodStart } from './periods';
import { stageFor } from './pipelines';
import { byId, hasId, idKey, idSet, lookup, ms, sameId } from './util';

export interface MetricsSource {
  activities: readonly ActivityPayload[];
  records: readonly RecordPayload[];
  visitResults: readonly VisitResultPayload[];
}

interface IndexedActivity {
  /** Lower-case `userId`. */
  user?: string;
  type: ActivityType;
  eff: number;
  contact: boolean;
  appointment: boolean;
}

interface IndexedRecord {
  owner?: string;
  createdBy?: string;
  won?: number;
  created?: number;
  value: number;
}

/** Pre-parsed completed activities and records (build once per data change, then ask many questions). */
export interface MetricsIndex {
  readonly activities: readonly IndexedActivity[];
  readonly records: readonly IndexedRecord[];
}

/** True when a visit result name means an appointment (`name.lowercased().contains("appointment")`). */
export function isAppointmentResult(result: Pick<VisitResultPayload, 'name'> | undefined): boolean {
  return !!result && result.name.toLowerCase().includes('appointment');
}

export function buildMetricsIndex(source: MetricsSource): MetricsIndex {
  const results = byId(source.visitResults);
  const activities: IndexedActivity[] = [];
  for (const a of source.activities) {
    if (a.status !== 'completed') continue;
    const result = lookup(results, a.resultId);
    const ia: IndexedActivity = {
      type: a.type,
      eff: effectiveMs(a),
      contact: result?.countsAsContact ?? false,
      appointment: a.type === 'meeting' || isAppointmentResult(result),
    };
    if (a.userId) ia.user = idKey(a.userId);
    activities.push(ia);
  }
  const records: IndexedRecord[] = source.records.map((r) => {
    const ir: IndexedRecord = { value: r.value };
    if (r.ownerId) ir.owner = idKey(r.ownerId);
    if (r.createdBy) ir.createdBy = idKey(r.createdBy);
    const won = ms(r.wonAt);
    if (won !== undefined) ir.won = won;
    const created = ms(r.createdAt);
    if (created !== undefined) ir.created = created;
    return ir;
  });
  return { activities, records };
}

function activityMatches(metric: GoalMetric, a: IndexedActivity): boolean {
  switch (metric) {
    case 'visits':
      return a.type === 'visit';
    case 'contacts':
      return a.type === 'visit' && a.contact;
    case 'appointments':
      return a.appointment;
    case 'calls':
      return a.type === 'call';
    case 'emails':
      return a.type === 'email';
    case 'texts':
      return a.type === 'text';
    default:
      return false;
  }
}

/**
 * `AppStore.metricValue(_:userId:from:to:)`. `userId` undefined/null = company totals. `to` defaults to now.
 */
export function metricValue(
  index: MetricsIndex,
  metric: GoalMetric,
  userId: string | null | undefined,
  from: Date,
  to: Date = new Date(),
): number {
  const u = userId ? idKey(userId) : undefined;
  const f = from.getTime();
  const t = to.getTime();
  switch (metric) {
    case 'deals':
    case 'revenue': {
      let n = 0;
      for (const r of index.records) {
        if (r.won === undefined || r.won < f || r.won > t) continue;
        if (u !== undefined && r.owner !== u) continue;
        n += metric === 'deals' ? 1 : r.value;
      }
      return n;
    }
    case 'newRecords': {
      let n = 0;
      for (const r of index.records) {
        if (r.created === undefined || r.created < f || r.created > t) continue;
        if (u !== undefined && r.createdBy !== u && r.owner !== u) continue;
        n += 1;
      }
      return n;
    }
    default: {
      let n = 0;
      for (const a of index.activities) {
        if (a.eff < f || a.eff > t) continue;
        if (u !== undefined && a.user !== u) continue;
        if (activityMatches(metric, a)) n += 1;
      }
      return n;
    }
  }
}

/** The `team_metrics` column for a metric. */
export function metricsRowValue(row: MetricsRow, metric: GoalMetric): number {
  return metric === 'newRecords' ? row.new_records : row[metric];
}

/**
 * Port of the `team_metrics` SQL over the replica: one row per profile that has anything in range (activities with
 * a `userId`, won records with an owner, new records by creator or owner), `profile_id` lower-case, sorted by it.
 * For every profile each column equals `metricValue(metric, profile_id, from, to)`.
 */
export function teamMetrics(index: MetricsIndex, from: Date, to: Date): MetricsRow[] {
  const f = from.getTime();
  const t = to.getTime();
  const rows = new Map<string, MetricsRow>();
  const row = (id: string): MetricsRow => {
    let r = rows.get(id);
    if (!r) {
      r = { profile_id: id, visits: 0, contacts: 0, appointments: 0, calls: 0, emails: 0, texts: 0, deals: 0, revenue: 0, new_records: 0 };
      rows.set(id, r);
    }
    return r;
  };
  for (const a of index.activities) {
    if (!a.user || a.eff < f || a.eff > t) continue;
    const r = row(a.user);
    if (a.type === 'visit') r.visits += 1;
    if (a.type === 'visit' && a.contact) r.contacts += 1;
    if (a.appointment) r.appointments += 1;
    if (a.type === 'call') r.calls += 1;
    if (a.type === 'email') r.emails += 1;
    if (a.type === 'text') r.texts += 1;
  }
  for (const rec of index.records) {
    if (rec.owner && rec.won !== undefined && rec.won >= f && rec.won <= t) {
      const r = row(rec.owner);
      r.deals += 1;
      r.revenue += rec.value;
    }
    if (rec.created !== undefined && rec.created >= f && rec.created <= t) {
      if (rec.createdBy) row(rec.createdBy).new_records += 1;
      if (rec.owner && rec.owner !== rec.createdBy) row(rec.owner).new_records += 1;
    }
  }
  return [...rows.values()].sort((a, b) => (a.profile_id < b.profile_id ? -1 : a.profile_id > b.profile_id ? 1 : 0));
}

// MARK: - People

/** `AppStore.RepDaySummary`. */
export interface RepDaySummary {
  visits: number;
  contacts: number;
  appointments: number;
  /** The day's `dayLogs.totalMeters`. */
  meters: number;
  /** Visits from the start of that week to the end of the day. */
  weekVisits: number;
  /** Non-archived stores the rep owns. */
  ownedRecords: number;
}

/** `DayLog.dayKey` equality: the log whose `date` falls on the same local calendar day. */
export function dayLogFor(dayLogs: readonly DayLogPayload[], userId: string, day: Date): DayLogPayload | undefined {
  return dayLogs.find((l) => {
    if (!sameId(l.userId, userId)) return false;
    const d = ms(l.date);
    return d !== undefined && isSameDay(new Date(d), day);
  });
}

/** `AppStore.repSummary(for:on:)`. */
export function repSummary(
  index: MetricsIndex,
  userId: string,
  data: { dayLogs: readonly DayLogPayload[]; records: readonly RecordPayload[] },
  date: Date = new Date(),
  opts: WeekOptions = {},
): RepDaySummary {
  const start = startOfDay(date);
  const end = endOfDay(date);
  const weekStart = startOfWeek(date, resolveWeekStart(opts));
  return {
    visits: metricValue(index, 'visits', userId, start, end),
    contacts: metricValue(index, 'contacts', userId, start, end),
    appointments: metricValue(index, 'appointments', userId, start, end),
    weekVisits: metricValue(index, 'visits', userId, weekStart, end),
    meters: dayLogFor(data.dayLogs, userId, date)?.totalMeters ?? 0,
    ownedRecords: data.records.filter((r) => !r.isArchived && sameId(r.ownerId, userId)).length,
  };
}

export interface LeaderboardEntry {
  user: UserPayload;
  value: number;
}

/**
 * `AppStore.leaderboard(metric:period:users:)`: active users with their value since the period start, highest first
 * (ties keep the given order). Pass `leaderboardUsers(...)` or the visible users.
 */
export function leaderboard(
  index: MetricsIndex,
  metric: GoalMetric,
  period: GoalPeriod,
  users: readonly UserPayload[],
  now: Date = new Date(),
  opts: WeekOptions = {},
): LeaderboardEntry[] {
  const from = goalPeriodStart(period, now, opts);
  return users
    .filter((u) => u.isActive)
    .map((user) => ({ user, value: metricValue(index, metric, user.id, from, now) }))
    .sort((a, b) => b.value - a.value);
}

/** `goalCurrentValue`: the goal's metric for its owner (company-wide when it has no `userId`) this period. */
export function goalCurrentValue(index: MetricsIndex, goal: GoalPayload, now: Date = new Date(), opts: WeekOptions = {}): number {
  return metricValue(index, goal.metric, goal.userId, goalPeriodStart(goal.period, now, opts), now);
}

/** `AppStore.goalProgress`: 0…1. A team goal (no `userId`) measures company-wide totals, as on iOS. */
export function goalProgress(index: MetricsIndex, goal: GoalPayload, now: Date = new Date(), opts: WeekOptions = {}): number {
  if (!(goal.target > 0)) return 0;
  return Math.min(1, goalCurrentValue(index, goal, now, opts) / goal.target);
}

/** `AppStore.earnedBadges(for:)`: badges whose metric this period reached the threshold. */
export function earnedBadges(
  index: MetricsIndex,
  badges: readonly BadgePayload[],
  userId: string,
  now: Date = new Date(),
  opts: WeekOptions = {},
): BadgePayload[] {
  return badges.filter((b) => metricValue(index, b.metric, userId, goalPeriodStart(b.period, now, opts), now) >= b.threshold);
}

/** `badgeProgress`: 0…1 toward the threshold this period. */
export function badgeProgress(
  index: MetricsIndex,
  badge: BadgePayload,
  userId: string,
  now: Date = new Date(),
  opts: WeekOptions = {},
): number {
  if (!(badge.threshold > 0)) return 0;
  return Math.min(1, metricValue(index, badge.metric, userId, goalPeriodStart(badge.period, now, opts), now) / badge.threshold);
}

export interface TeamFeedOptions {
  /** Narrow to these people (any case). */
  userIds?: Iterable<string>;
  types?: Iterable<ActivityType>;
  /** Keep the newest N. */
  limit?: number;
}

/** `teamFeed`: completed activities by the visible users, newest first by effective date. */
export function teamFeed(
  activities: readonly ActivityPayload[],
  visibleUsers: readonly Pick<UserPayload, 'id'>[],
  opts: TeamFeedOptions = {},
): ActivityPayload[] {
  const visible = idSet(visibleUsers.map((u) => u.id));
  const only = opts.userIds ? idSet(opts.userIds) : null;
  const types = opts.types ? new Set(opts.types) : null;
  const out = activities
    .filter((a) => {
      if (a.status !== 'completed' || !a.userId || !hasId(visible, a.userId)) return false;
      if (only && !hasId(only, a.userId)) return false;
      if (types && !types.has(a.type)) return false;
      return true;
    })
    .map((a) => ({ a, eff: effectiveMs(a) }))
    .sort((x, y) => y.eff - x.eff)
    .map((x) => x.a);
  return opts.limit !== undefined ? out.slice(0, Math.max(0, opts.limit)) : out;
}

// MARK: - Reports (ReportEngine.Filter / summary)

export interface ReportFilter {
  /** Range start (`DateRange.start`). */
  from: Date;
  /** Optional end (iOS ranges have none; the web's custom range does). */
  to?: Date;
  /** Only these people (undefined = every visible user). */
  userIds?: readonly string[];
  teamId?: string;
  territoryId?: string;
}

export interface ReportData {
  /** `visibleUsers(...)` for the viewer. */
  visibleUsers: readonly UserPayload[];
  activities: readonly ActivityPayload[];
  records: readonly RecordPayload[];
  visitResults: readonly VisitResultPayload[];
  pipelines: readonly PipelinePayload[];
  dayLogs: readonly DayLogPayload[];
}

function inRange(t: number | undefined, f: ReportFilter): boolean {
  if (t === undefined || t < f.from.getTime()) return false;
  return f.to === undefined || t <= f.to.getTime();
}

/** `ReportEngine.scopedUsers`. */
export function scopedUsers(data: Pick<ReportData, 'visibleUsers'>, f: ReportFilter): UserPayload[] {
  let users = [...data.visibleUsers];
  if (f.userIds) {
    const ids = idSet(f.userIds);
    users = users.filter((u) => hasId(ids, u.id));
  }
  if (f.teamId) users = users.filter((u) => sameId(u.teamId, f.teamId));
  return users;
}

/** `ReportEngine.scopedActivities`: completed, in range, by a scoped user, optionally typed and in a territory. */
export function scopedActivities(
  data: Pick<ReportData, 'visibleUsers' | 'activities' | 'records'>,
  f: ReportFilter,
  types?: readonly ActivityType[],
): ActivityPayload[] {
  const userIds = idSet(scopedUsers(data, f).map((u) => u.id));
  const terr = f.territoryId ? idSet(data.records.filter((r) => sameId(r.territoryId, f.territoryId)).map((r) => r.id)) : null;
  return data.activities.filter(
    (a) =>
      a.status === 'completed' &&
      inRange(effectiveMs(a), f) &&
      !!a.userId &&
      hasId(userIds, a.userId) &&
      (!types || types.includes(a.type)) &&
      (!terr || hasId(terr, a.recordId)),
  );
}

/** `ReportEngine.scopedRecords`: not archived, unowned or owned by a scoped user, in the territory when given. */
export function scopedRecords(data: Pick<ReportData, 'visibleUsers' | 'records'>, f: ReportFilter): RecordPayload[] {
  const userIds = idSet(scopedUsers(data, f).map((u) => u.id));
  return data.records.filter(
    (r) => !r.isArchived && (!r.ownerId || hasId(userIds, r.ownerId)) && (!f.territoryId || sameId(r.territoryId, f.territoryId)),
  );
}

/** `ReportEngine.Summary`. */
export interface ReportSummary {
  visits: number;
  contacts: number;
  appointments: number;
  calls: number;
  emails: number;
  texts: number;
  recordsCreated: number;
  recordsWon: number;
  revenueWon: number;
  pipelineValue: number;
  weightedPipeline: number;
  /** contacts / visits (0 without visits). */
  contactRate: number;
  /** appointments / contacts (0 without contacts). */
  appointmentRate: number;
  /** verified visits / visits, 0…1. */
  verifiedRate: number;
  totalMeters: number;
}

/**
 * `ReportEngine.summary`. One deliberate difference: a record counts as won only when it has a `wonAt` (iOS compares
 * `wonAt ?? .distantPast` with the range start, so "All Time" counts every store as won).
 */
export function reportSummary(data: ReportData, f: ReportFilter): ReportSummary {
  const results = byId(data.visitResults);
  const acts = scopedActivities(data, f);
  const visits = acts.filter((a) => a.type === 'visit');
  const contacts = visits.filter((a) => lookup(results, a.resultId)?.countsAsContact ?? false).length;
  const appointments = acts.filter((a) => a.type === 'meeting' || isAppointmentResult(lookup(results, a.resultId))).length;
  const recs = scopedRecords(data, f);
  const won = recs.filter((r) => inRange(ms(r.wonAt), f));
  let pipelineValue = 0;
  let weightedPipeline = 0;
  for (const r of recs) {
    const st = stageFor(r, data.pipelines);
    if (st?.isWon || st?.isLost) continue;
    pipelineValue += r.value;
    weightedPipeline += (r.value * (st?.probability ?? 0)) / 100;
  }
  const userIds = idSet(scopedUsers(data, f).map((u) => u.id));
  // iOS has no end bound (its ranges end now); the web's custom range must also stop counting after its last day.
  const totalMeters = data.dayLogs
    .filter((l) => hasId(userIds, l.userId) && inRange(ms(l.date), f))
    .reduce((sum, l) => sum + l.totalMeters, 0);
  return {
    visits: visits.length,
    contacts,
    appointments,
    calls: acts.filter((a) => a.type === 'call').length,
    emails: acts.filter((a) => a.type === 'email').length,
    texts: acts.filter((a) => a.type === 'text').length,
    recordsCreated: recs.filter((r) => inRange(ms(r.createdAt), f)).length,
    recordsWon: won.length,
    revenueWon: won.reduce((sum, r) => sum + r.value, 0),
    pipelineValue,
    weightedPipeline,
    contactRate: visits.length > 0 ? contacts / visits.length : 0,
    appointmentRate: contacts > 0 ? appointments / contacts : 0,
    verifiedRate: visits.length > 0 ? visits.filter((a) => isVerified(a)).length / visits.length : 0,
    totalMeters,
  };
}
